import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { MovimentoConsultaDTO } from '../../../../../models/movimentacao.dto';
import { MovimentacaoService } from '../../../../../services/financeiro/movimentacao.service';

@Component({
  selector: 'app-pessoa-movimentacao-table',
  templateUrl: './movimentacao-table.component.html',
  styleUrls: ['./movimentacao-table.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class MovimentacaoPessoaTableComponent implements OnInit, OnDestroy {
  @Input() pessoaId: number | undefined;
  movimentacao: MovimentoConsultaDTO[] = [] as MovimentoConsultaDTO[];
  first = 0;
  rows = 5;
  totalRecords = 0;
  lastfield = '';
  lastSort = '';
  searchCodigo: string = '';
  options = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];
  tipoDeTransacao: { label: string; value: string }[] = [];
  transacaoSelecionada = { label: '', value: 'DESPESA,RECEITA' };
  rangeDates: any[] = [];
  private langChangeSub!: Subscription;

  // Objetos de traducao
  placeholders = {
    tipo_transacao: '',
    pesquisar_codigo: '',
    pesquisar_data: ''
  };

  botoes = {
    buscar: ''
  };

  headers = {
    codigo_movimento: '',
    tipo_transacao: '',
    valor_total: '',
    moeda_da_conta: '',
    moeda_da_conta_destino: '',
    data_inclusao: '',
    acoes: ''
  };

  tooltips = {
    relatorio_pdf: ''
  };

  texts = {
    itens_por_pagina: ''
  };

  tipoTransacaoEnum: { [key: string]: string } = {};

  constructor(
    private router: Router,
    private movimentacaoService: MovimentacaoService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    if (!this.pessoaId) {
      return;
    }
    await this.findMovimentacao(0, this.rows, 'id,asc');
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations() {
    // Carregar filtros de tipo de transacao
    this.tipoDeTransacao = [
      { label: this.translate.instant('shared.movimentacao-table.filtros.todas'), value: 'DESPESA,RECEITA' },
      { label: this.translate.instant('shared.movimentacao-table.filtros.receita'), value: 'RECEITA' },
      { label: this.translate.instant('shared.movimentacao-table.filtros.despesa'), value: 'DESPESA' },
    ];
    this.transacaoSelecionada = this.tipoDeTransacao[0];

    // Carregar placeholders
    this.placeholders = {
      tipo_transacao: this.translate.instant('shared.movimentacao-table.placeholders.tipo_transacao'),
      pesquisar_codigo: this.translate.instant('shared.movimentacao-table.placeholders.pesquisar_codigo'),
      pesquisar_data: this.translate.instant('shared.movimentacao-table.placeholders.pesquisar_data')
    };

    // Carregar botoes
    this.botoes = {
      buscar: this.translate.instant('shared.movimentacao-table.botoes.buscar')
    };

    // Carregar headers
    this.headers = {
      codigo_movimento: this.translate.instant('shared.movimentacao-table.headers.codigo_movimento'),
      tipo_transacao: this.translate.instant('shared.movimentacao-table.headers.tipo_transacao'),
      valor_total: this.translate.instant('shared.movimentacao-table.headers.valor_total'),
      moeda_da_conta: this.translate.instant('shared.movimentacao-table.headers.moeda_da_conta'),
      moeda_da_conta_destino: this.translate.instant('shared.movimentacao-table.headers.moeda_da_conta_destino'),
      data_inclusao: this.translate.instant('shared.movimentacao-table.headers.data_inclusao'),
      acoes: this.translate.instant('shared.movimentacao-table.headers.acoes')
    };

    // Carregar tooltips
    this.tooltips = {
      relatorio_pdf: this.translate.instant('shared.movimentacao-table.tooltips.relatorio_pdf')
    };

    // Carregar texts
    this.texts = {
      itens_por_pagina: this.translate.instant('shared.movimentacao-table.itens_por_pagina')
    };

    // Carregar traducoes dos valores do enum TipoTransacao
    const chaves = ['RECEITA', 'DESPESA', 'TRANSFERENCIA', 'CAMBIO', 'VENDA', 'DEVOLUCAO', 'COMPRA', 'DEVOLUCAO_DE_COMPRA'];
    this.tipoTransacaoEnum = {};
    chaves.forEach(chave => {
      this.tipoTransacaoEnum[chave] = this.translate.instant(`shared.movimentacao-table.tipo_transacao_enum.${chave}`);
    });
  }

  traduzirTipoTransacao(tipo: any): string {
    // O backend pode retornar o enum como objeto { enum: string } ou como string direta
    const tipoStr: string = (tipo && typeof tipo === 'object' && 'enum' in tipo)
      ? String(tipo.enum).toUpperCase()
      : String(tipo ?? '').toUpperCase();
    return this.tipoTransacaoEnum[tipoStr] || tipoStr;
  }
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  search() {
    this.findMovimentacao(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  findMovimentacao(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ): Promise<void> {
    return new Promise((resolve) => {
      const codigoMovimento = this.searchCodigo
        ? `&codigoMovimento=${this.searchCodigo}`
        : '';
      const transacaoSelecionada = this.transacaoSelecionada.value
        ? `&tipoDeTransacao=${this.transacaoSelecionada.value}`
        : '';
      const dataInicio =
        this.rangeDates.length > 0
          ? `&dataInicio=${this.formatDate(this.rangeDates[0])}`
          : '&dataInicio=2024-01-01';
      const dataFim =
        this.rangeDates.length > 0
          ? `&dataFim=${this.formatDate(this.rangeDates[1])}`
          : '&dataFim=2099-01-01';

      const filter =
        `pessoaId=${this.pessoaId}` +
        `${codigoMovimento}` +
        `${transacaoSelecionada}` +
        `${dataInicio}` +
        `${dataFim}`;
      this.movimentacaoService
        .get('/movimento/buscar', { page, size, sort, filter })
        .subscribe(
          (response) => {
            this.movimentacao = response.content;
            this.totalRecords = response.totalElements; // Atualiza o total de registros
            this.first = page * size;
            this.rangeDates = [];
            resolve();
          },
          (error) => {
            console.error('Erro ao buscar clientes:', error);
            this.movimentacao = [];
            this.totalRecords = 0;
            this.rangeDates = [];
          }
        );
    });
  }

  onPageChange(event: any) {
    const page = event.page; // Página atual a partir do paginator
    const size = event.rows; // Quantidade de itens por página
    this.findMovimentacao(page, size);
  }

  onRowsPerPageChange() {
    this.first = 0; // Reiniciar a página para o início
    this.findMovimentacao(0, this.rows); // Buscar a primeira página com o novo tamanho
  }

  customSort(event: SortEvent) {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    } // 1 é ascendente, -1 é descendente}

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    } // Essencial para não cair em loop infinito

    this.lastfield = String(event.field);
    this.lastSort = String(sort);

    this.findMovimentacao(0, this.rows, `${event.field},${sort}`);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0'); // Preenche com zero à esquerda
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses começam em 0
    const year = String(date.getFullYear()).slice(-4); // Pega os últimos 2 dígitos do ano
    return `${year}-${month}-${day}`;
  }
}
